import {useEffect, useLayoutEffect, useRef, useState, type CSSProperties} from 'react';
import {createPortal} from 'react-dom';
import {MoneyLabel} from '../../../components/general/MoneyLabel.tsx';
import type {Money} from '../../../lib/Money.ts';
import {
    formatExchangeRate,
    formatMarketDate,
    wasConvertedByHand,
    type MonetaryOrigin,
} from '../../../components/general/ForeignAmountLabel.tsx';

const POPOVER_GAP_PX = 4;
const VIEWPORT_MARGIN_PX = 8;

function popoverPositionFor(trigger: DOMRect, popover: DOMRect): CSSProperties {
    const fitsBelow = trigger.bottom + POPOVER_GAP_PX + popover.height <= window.innerHeight - VIEWPORT_MARGIN_PX;
    const top = fitsBelow
        ? trigger.bottom + POPOVER_GAP_PX
        : Math.max(VIEWPORT_MARGIN_PX, trigger.top - POPOVER_GAP_PX - popover.height);
    const left = Math.max(
        VIEWPORT_MARGIN_PX,
        Math.min(trigger.left, window.innerWidth - VIEWPORT_MARGIN_PX - popover.width),
    );
    return {top, left};
}

interface Props {
    origin: MonetaryOrigin;
    // A line's own billed amount. Left out on the header, which shows the bill's.
    billedAmount?: Money | null;
    withLabel?: boolean;
}

export function ConvertedAmount({origin, billedAmount, withLabel = true}: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const [popoverPosition, setPopoverPosition] = useState<CSSProperties | null>(null);
    const containerRef = useRef<HTMLSpanElement>(null);
    const popoverRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const closeOnOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (!containerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
                setIsOpen(false);
            }
        };
        const close = () => setIsOpen(false);
        document.addEventListener('mousedown', closeOnOutsideClick);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen || !containerRef.current || !popoverRef.current) {
            setPopoverPosition(null);
            return;
        }
        setPopoverPosition(popoverPositionFor(
            containerRef.current.getBoundingClientRect(),
            popoverRef.current.getBoundingClientRect(),
        ));
    }, [isOpen]);
    const billed = billedAmount !== undefined ? billedAmount : origin.original_fx_total_amount_payable;
    const byHand = wasConvertedByHand(origin);
    const rate = origin.fx_rate === null || byHand ? null : formatExchangeRate(origin.fx_rate);

    return (
        <span ref={containerRef} className="relative inline-flex items-center gap-1 text-xs text-gray-500">
            <button
                type="button"
                aria-label="Toon omrekening"
                aria-expanded={isOpen}
                onClick={() => setIsOpen(!isOpen)}
                className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
                <i className="ri-exchange-line text-sm leading-none" aria-hidden="true"/>
            </button>
            {withLabel && (byHand ? 'handmatig omgerekend' : 'omgerekend naar euro')}
            {isOpen && createPortal(
                <span
                    ref={popoverRef}
                    role="tooltip"
                    style={popoverPosition ?? {top: 0, left: 0, visibility: 'hidden'}}
                    className="fixed z-[9999] w-60 rounded-lg bg-white p-3 text-left text-xs leading-5 text-gray-600 shadow-lg ring-1 ring-black ring-opacity-5"
                >
                    <span className="block font-medium text-gray-900">
                        {byHand ? 'Handmatig omgerekend' : 'Omgerekend naar euro'}
                    </span>
                    {billed != null && (
                        <span className="block">
                            Oorspronkelijk bedrag:{' '}
                            <MoneyLabel
                                value={billed.signed_amount / 100}
                                currency={billed.currency}
                                showCurrency={true}
                            />
                        </span>
                    )}
                    {rate !== null && (
                        <span className="block">
                            Koers: {rate} {origin.original_fx_currency}/EUR
                        </span>
                    )}
                    {origin.fx_rate_market_date !== null && (
                        <span className="block">
                            Koersdatum: {formatMarketDate(origin.fx_rate_market_date)}
                        </span>
                    )}
                </span>,
                document.body,
            )}
        </span>
    );
}
